/* ==========================================================================
   Black Mango — Scroll Animations (Awwwards Edition)
   GSAP ScrollTrigger orchestration. Requires gsap + ScrollTrigger globals.
   11 animation groups: hero chars, brand-story morph, showcase parallax,
   general reveals, founder alternating slide, header, progress bar,
   section color transitions, clip-path reveals, parallax images,
   grid diagonal stagger.
   ========================================================================== */

(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn('[BlackMango] GSAP/ScrollTrigger missing — animations disabled.');
    return;
  }
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    document.querySelectorAll(
      '.reveal,.reveal-word,.hero-immersive__heading,.hero-immersive__subheading,' +
      '.founders-note__line,.founders-note__quote-mark,.reveal-clip'
    ).forEach(function (el) { el.style.opacity = '1'; el.style.transform = 'none'; });
    return;
  }
  gsap.registerPlugin(ScrollTrigger);

  const triggers = [];
  const MOBILE = 1024;
  function q(s) { return document.querySelector(s); }
  function qAll(s) { return document.querySelectorAll(s); }

  /* 1. HERO — SplitText-style char animation or fade-up fallback ---------- */
  function initHeroReveal() {
    const heading = q('.hero-immersive__heading');
    const sub = q('.hero-immersive__subheading');
    if (!heading && !sub) return;
    const tl = gsap.timeline({ delay: 0.3 });

    if (heading) {
      let chars = heading.querySelectorAll('.char');
      if (chars.length === 0 && heading.hasAttribute('data-split-text')) {
        heading.innerHTML = heading.textContent.replace(/\S/g, function (c) {
          return '<span class="char" style="display:inline-block;overflow:hidden">' + c + '</span>';
        });
        chars = heading.querySelectorAll('.char');
      }
      if (chars.length > 0) {
        gsap.set(heading, { opacity: 1 });
        gsap.set(chars, { y: '100%', rotateX: -80, opacity: 0, transformOrigin: '50% 100%' });
        tl.to(chars, { y: '0%', rotateX: 0, opacity: 1, duration: 1, ease: 'power4.out',
          stagger: { each: 0.03, from: 'random' } });
      } else {
        gsap.set(heading, { opacity: 0, y: 40 });
        tl.to(heading, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out' });
      }
    }
    if (sub) {
      gsap.set(sub, { opacity: 0, y: 30 });
      tl.to(sub, { opacity: 1, y: 0, duration: 1, ease: 'power3.out' }, '-=0.5');
    }
  }

  /* 2. BRAND STORY — masked word slide-up + background color morph -------- */
  function initBrandStoryReveal() {
    const section = q('.brand-story');
    const sticky = q('.brand-story__sticky');
    const words = qAll('.brand-story__word');
    if (!sticky || words.length === 0) return;

    for (const word of words) {
      if (!word.parentElement.classList.contains('brand-story__word-mask')) {
        const mask = document.createElement('span');
        mask.className = 'brand-story__word-mask';
        mask.style.cssText = 'display:inline-block;overflow:hidden;vertical-align:bottom;';
        word.parentNode.insertBefore(mask, word);
        mask.appendChild(word);
      }
    }
    const tl = gsap.timeline({ scrollTrigger: {
      trigger: '.brand-story', start: 'top top',
      end: function () { return '+=' + window.innerHeight * 2; },
      pin: sticky, scrub: true,
    }});
    triggers.push(tl.scrollTrigger);

    if (section) {
      tl.to(sticky, { backgroundColor: '#0F0F0F', duration: 0.5, ease: 'none' }, 0);
      tl.to(sticky, { backgroundColor: '#1C1C1C', duration: 0.5, ease: 'none' }, 0.5);
    }
    words.forEach(function (word) {
      const attr = word.getAttribute('data-word');
      let color = 'var(--color-ivory)';
      if (attr === 'mango') color = 'var(--color-amber)';
      gsap.set(word, { y: '110%', opacity: 0.15, color: 'var(--color-mist)', display: 'inline-block' });
      tl.to(word, { y: '0%', opacity: 1, color: color, duration: 0.8, ease: 'power2.out' });
    });
  }

  /* 3. FRAGRANCE SHOWCASE — horizontal scroll + parallax depth ------------ */
  function initFragranceShowcase() {
    const wrapper = q('.fragrance-showcase__wrapper');
    const track = q('.fragrance-showcase__track');
    if (!wrapper || !track) return;

    gsap.matchMedia().add('(min-width: ' + MOBILE + 'px)', function () {
      const panels = track.querySelectorAll('.fragrance-showcase__panel');
      if (panels.length === 0) return;
      const totalScroll = track.scrollWidth - window.innerWidth;

      const mainTl = gsap.timeline({ scrollTrigger: {
        trigger: wrapper, start: 'top top',
        end: function () { return '+=' + totalScroll; },
        pin: true, scrub: true, snap: 1 / (panels.length - 1), invalidateOnRefresh: true,
      }});
      triggers.push(mainTl.scrollTrigger);
      mainTl.to(track, { x: function () { return -totalScroll; }, ease: 'none' });

      panels.forEach(function (panel) {
        const img = panel.querySelector('.fragrance-showcase__image');
        const topN = panel.querySelectorAll('.fragrance-showcase__notes--top .fragrance-showcase__note');
        const heartN = panel.querySelectorAll('.fragrance-showcase__notes--heart .fragrance-showcase__note');
        const baseN = panel.querySelectorAll('.fragrance-showcase__notes--base .fragrance-showcase__note');
        const pTl = gsap.timeline({ scrollTrigger: {
          trigger: panel, containerAnimation: mainTl,
          start: 'left center', end: 'right center', scrub: true,
        }});
        triggers.push(pTl.scrollTrigger);

        if (img) pTl.fromTo(img, { x: '10%' }, { x: '-10%', ease: 'none', duration: 1 }, 0);
        pTl.fromTo(panel.querySelector('.fragrance-showcase__panel-inner') || panel,
          { opacity: 0.7 }, { opacity: 1, duration: 0.5, ease: 'power1.inOut', yoyo: true, repeat: 1 }, 0);

        if (topN.length) { gsap.set(topN, { y: -30, opacity: 0 }); pTl.to(topN, { y: 0, opacity: 1, stagger: 0.1, duration: 1 }, 0); }
        if (heartN.length) { gsap.set(heartN, { scale: 0.9, opacity: 0 }); pTl.to(heartN, { scale: 1, opacity: 1, stagger: 0.1, duration: 1 }, 0.15); }
        if (baseN.length) { gsap.set(baseN, { y: 30, opacity: 0 }); pTl.to(baseN, { y: 0, opacity: 1, stagger: 0.1, duration: 1 }, 0.3); }
      });
    });
  }

  /* 4. GENERAL REVEAL — IntersectionObserver ------------------------------ */
  function initGeneralReveals() {
    const reveals = qAll('.reveal');
    if (reveals.length === 0) return;
    const observer = new IntersectionObserver(function (entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        if (entry.target.hasAttribute('data-stagger')) {
          const ch = entry.target.children;
          for (let i = 0; i < ch.length; i++) {
            ch[i].style.transitionDelay = i * 0.1 + 's';
            ch[i].classList.add('is-visible');
          }
        }
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15 });
    for (const el of reveals) observer.observe(el);
  }

  /* 5. FOUNDER'S NOTE — alternating slide + quote overshoot --------------- */
  function initFoundersNote() {
    const section = q('.founders-note');
    const lines = qAll('.founders-note__line');
    const quote = q('.founders-note__quote-mark');
    if (lines.length === 0 && !quote) return;

    if (quote) {
      gsap.set(quote, { scale: 0, opacity: 0, transformOrigin: '50% 50%' });
      const st = ScrollTrigger.create({ trigger: section || '.founders-note', start: 'top 85%', once: true,
        onEnter: function () { gsap.to(quote, { scale: 1, opacity: 0.3, duration: 1.2, ease: 'back.out(2.5)' }); }
      });
      triggers.push(st);
    }
    if (lines.length > 0) {
      lines.forEach(function (line, i) {
        gsap.set(line, { opacity: 0, x: i % 2 === 0 ? -60 : 60, rotate: i % 2 === 0 ? -2 : 2 });
      });
      const st = ScrollTrigger.create({ trigger: section || '.founders-note', start: 'top 75%', once: true,
        onEnter: function () { gsap.to(lines, { opacity: 1, x: 0, rotate: 0, duration: 1, ease: 'power3.out', stagger: 0.15 }); }
      });
      triggers.push(st);
    }
  }

  /* 6. HEADER SCROLL — show/hide on direction ---------------------------- */
  function initHeaderScroll() {
    const header = q('.header');
    if (!header) return;
    const st = ScrollTrigger.create({ start: 0, end: 'max', onUpdate: function (self) {
      const scroll = self.scroll();
      header.classList.toggle('header--scrolled', scroll > 100);
      if (self.direction === -1) header.classList.add('header--visible');
      else if (scroll > 100) header.classList.remove('header--visible');
    }});
    triggers.push(st);
  }

  /* 7. CUSTOM SCROLLBAR — right side, thin track + thumb ----------------- */
  function initScrollProgress() {
    /* Track */
    const track = document.createElement('div');
    track.setAttribute('aria-hidden', 'true');
    track.className = 'bm-scrollbar-track';
    track.style.cssText =
      'position:fixed;top:0;right:0;width:6px;height:100%;z-index:9999;' +
      'pointer-events:none;background:transparent;';

    /* Thumb */
    const thumb = document.createElement('div');
    thumb.className = 'bm-scrollbar-thumb';
    thumb.style.cssText =
      'position:absolute;right:1px;width:4px;height:15%;top:0;' +
      'border-radius:4px;background:rgba(255,255,255,0.15);' +
      'transition:background 0.3s ease,opacity 0.4s ease;opacity:0;';
    track.appendChild(thumb);

    document.body.appendChild(track);

    let hideTimer = null;
    let lastThumbH = 0;
    const st = ScrollTrigger.create({ start: 0, end: 'max', onUpdate: function (self) {
      const viewRatio = window.innerHeight / document.documentElement.scrollHeight;
      const thumbH = Math.max(viewRatio * 100, 8);
      const thumbTop = self.progress * (100 - thumbH);

      if (Math.abs(thumbH - lastThumbH) > 0.5) {
        thumb.style.height = thumbH.toFixed(2) + '%';
        lastThumbH = thumbH;
      }
      thumb.style.top = thumbTop.toFixed(2) + '%';

      if (thumb.style.opacity !== '1') {
        thumb.style.opacity = '1';
        thumb.style.background = 'rgba(255,255,255,0.25)';
      }
      clearTimeout(hideTimer);
      hideTimer = setTimeout(function() {
        thumb.style.opacity = '0';
      }, 1200);
    }});
    triggers.push(st);
  }

  /* 8. SECTION COLOR TRANSITIONS — data-bg-color mood morphing ------------ */
  function initSectionColorTransitions() {
    const sections = qAll('[data-bg-color]');
    if (sections.length === 0) return;
    let bgLayer = q('.bm-bg-layer');
    if (!bgLayer) {
      bgLayer = document.createElement('div');
      bgLayer.className = 'bm-bg-layer';
      bgLayer.setAttribute('aria-hidden', 'true');
      bgLayer.style.cssText = 'position:fixed;inset:0;z-index:-1;pointer-events:none;background-color:#050505;';
      document.body.prepend(bgLayer);
    }
    for (const sec of sections) {
      const color = sec.getAttribute('data-bg-color');
      if (!color) continue;
      const morph = function () { gsap.to(bgLayer, { backgroundColor: color, duration: 0.8, ease: 'power1.inOut' }); };
      const st = ScrollTrigger.create({ trigger: sec, start: 'top 60%', end: 'bottom 40%', onEnter: morph, onEnterBack: morph });
      triggers.push(st);
    }
  }

  /* 9. IMAGE CLIP-PATH REVEALS -------------------------------------------- */
  function initClipPathReveals() {
    const els = qAll('.reveal-clip');
    if (els.length === 0) return;
    for (const el of els) {
      gsap.set(el, { clipPath: 'inset(0 100% 0 0)' });
      const st = ScrollTrigger.create({ trigger: el, start: 'top 80%', once: true,
        onEnter: function () { gsap.to(el, { clipPath: 'inset(0 0% 0 0)', duration: 1.2, ease: 'power4.out' }); }
      });
      triggers.push(st);
    }
  }

  /* 10. PARALLAX IMAGES — [data-parallax] y-axis depth -------------------- */
  function initParallaxImages() {
    const els = qAll('[data-parallax]');
    if (els.length === 0) return;
    for (const el of els) {
      const speed = parseFloat(el.getAttribute('data-parallax')) || 0.2;
      const tw = gsap.to(el, {
        y: function () { return window.innerHeight * speed * -1; }, ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: 0.5, invalidateOnRefresh: true },
      });
      if (tw.scrollTrigger) triggers.push(tw.scrollTrigger);
    }
  }

  /* 12. STAGGERED GRID REVEALS — diagonal cascade ------------------------- */
  function initStaggeredGridReveals() {
    const grids = qAll('.collection-grid__grid');
    if (grids.length === 0) return;
    for (const grid of grids) {
      const items = grid.querySelectorAll('.collection-grid__item');
      if (items.length === 0) continue;
      const cols = (getComputedStyle(grid).gridTemplateColumns || '1fr').split(' ').length;
      gsap.set(items, { opacity: 0, y: 40 });
      const st = ScrollTrigger.create({ trigger: grid, start: 'top 80%', once: true, onEnter: function () {
        items.forEach(function (item, i) {
          const diag = (Math.floor(i / cols) + (i % cols)) * 0.1;
          gsap.to(item, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: diag });
        });
      }});
      triggers.push(st);
    }
  }

  /* ---- CLEANUP ---------------------------------------------------------- */
  function destroy() {
    for (const st of triggers) { if (st && typeof st.kill === 'function') st.kill(); }
    triggers.length = 0;
    const bar = q('.bm-scroll-progress'); if (bar) bar.remove();
    const bg = q('.bm-bg-layer'); if (bg) bg.remove();
  }

  /* ---- INIT ------------------------------------------------------------- */
  function init() {
    initHeroReveal();
    initBrandStoryReveal();
    initFragranceShowcase();
    initGeneralReveals();
    initFoundersNote();
    initHeaderScroll();
    initScrollProgress();
    initSectionColorTransitions();
    initClipPathReveals();
    initParallaxImages();
    initStaggeredGridReveals();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.BlackMangoAnimations = { destroy: destroy, reinit: function () { destroy(); init(); } };
})();
